import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Radar, Menu, X, Linkedin, Twitter, Facebook, Instagram, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/services", label: "Our Services" },
  { to: "/industries", label: "Industries" },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/contact", label: "Contact Us" },
];

export function Logo({ compact = false }) {
  return (
    <Link to="/" className="flex items-center gap-2.5" data-testid="logo">
      <span className="relative flex items-center justify-center w-9 h-9 rounded-md bg-blue-600/20 border border-blue-500/30">
        <Radar className="w-5 h-5 text-cyan-400" />
      </span>
      {!compact && (
        <span className="font-display font-bold text-lg tracking-tight text-white">
          GEOTRAQ <span className="text-cyan-400">AI</span>
        </span>
      )}
    </Link>
  );
}

export default function PublicLayout() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    setOpen(false);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="fixed top-0 inset-x-0 z-50 glass border-x-0 border-t-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between">
          <Logo />
          <nav className="hidden lg:flex items-center gap-7" data-testid="main-nav">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors ${isActive ? "text-cyan-400" : "text-slate-300 hover:text-white"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-3">
            <Link to="/login" data-testid="nav-client-login">
              <Button variant="ghost" className="text-slate-300 hover:text-white hover:bg-white/5">Client Login</Button>
            </Link>
            <Link to="/contact" data-testid="nav-launch-campaign">
              <Button className="bg-blue-600 hover:bg-blue-500 text-white font-semibold">
                Launch Your Campaign <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </Link>
          </div>
          <button className="lg:hidden text-white p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn" aria-label="Menu">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        {open && (
          <div className="lg:hidden glass border-x-0 px-6 py-4 space-y-1" data-testid="mobile-menu">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} className="block py-2.5 text-sm font-medium text-slate-200 hover:text-cyan-400">
                {n.label}
              </NavLink>
            ))}
            <div className="pt-3 flex gap-3">
              <Link to="/login" className="flex-1"><Button variant="outline" className="w-full border-white/10">Client Login</Button></Link>
              <Link to="/contact" className="flex-1"><Button className="w-full bg-blue-600 hover:bg-blue-500">Launch Campaign</Button></Link>
            </div>
          </div>
        )}
      </header>

      <main className="pt-[72px]">
        <Outlet />
      </main>

      <footer className="border-t border-white/5 bg-[#05090f]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
            <div className="md:col-span-2">
              <Logo />
              <p className="mt-4 text-sm text-muted-foreground max-w-sm leading-relaxed">
                Interactive Geofencing &amp; Geolocation Advertising. Put your brand where your customers are.
              </p>
              <div className="flex gap-3 mt-6">
                {[Linkedin, Twitter, Facebook, Instagram].map((Icon, i) => (
                  <a key={i} href="#" aria-label="Social link" data-testid={`social-${i}`}
                    className="w-9 h-9 rounded-md border border-white/10 flex items-center justify-center text-slate-400 hover:text-cyan-400 hover:border-cyan-500/40 transition-colors">
                    <Icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold tracking-[0.2em] text-slate-500 uppercase mb-4">Platform</p>
              <ul className="space-y-2.5 text-sm">
                {[["Home", "/"], ["About Us", "/about"], ["Our Services", "/services"], ["Industries", "/industries"], ["How It Works", "/how-it-works"], ["Contact Us", "/contact"]].map(([label, to]) => (
                  <li key={to}><Link to={to} className="text-slate-400 hover:text-cyan-400 transition-colors">{label}</Link></li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-xs font-semibold tracking-[0.2em] text-slate-500 uppercase mb-4">Access</p>
              <ul className="space-y-2.5 text-sm">
                <li><Link to="/login" className="text-slate-400 hover:text-cyan-400 transition-colors">Client Login</Link></li>
                <li><Link to="/privacy" className="text-slate-400 hover:text-cyan-400 transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-slate-400 hover:text-cyan-400 transition-colors">Terms &amp; Conditions</Link></li>
                <li><Link to="/cookies" className="text-slate-400 hover:text-cyan-400 transition-colors">Cookie Policy</Link></li>
              </ul>
            </div>
          </div>
          <div className="mt-12 pt-6 border-t border-white/5 flex flex-col sm:flex-row justify-between gap-3 text-xs text-slate-500">
            <p>© 2026 GEOTRAQ AI. All rights reserved.</p>
            <p>POPIA &amp; GDPR aligned. Aggregated analytics only — no personally identifiable location data.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
